import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PartnerContactInviteInput } from "@colab/shared";

export type InvitePartnerContactInput = PartnerContactInviteInput & {
  partnerId: string;
};

export type InvitePartnerContactResponse = {
  partnerUserId: string;
  token: string;
  inviteUrl: string;
};

// POST /api/partners/:id/users/invite
//
// Returns the raw token + inviteUrl so the admin can copy/share the link
// alongside the dispatched PARTNER_INVITED notification. Server fires the
// event via c.executionCtx.waitUntil; no client concern.
export function useInvitePartnerContact() {
  const queryClient = useQueryClient();
  return useMutation<
    InvitePartnerContactResponse,
    ApiClientError,
    InvitePartnerContactInput
  >({
    mutationFn: ({ partnerId, ...body }) =>
      apiFetch<InvitePartnerContactResponse>(`/partners/${partnerId}/users/invite`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, { partnerId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.users(partnerId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.detail(partnerId) });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to invite contact");
    },
  });
}
